import { Checkbox, ConfigProvider } from 'antd';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePlanningSession } from '../../../serverState/planning';
import { PlanningSessionActivityStep } from '../../../types/PlanningSessionType';
import { useGetActualSpaceId } from '../../../serverState/workspace';
import { useParams } from 'react-router-dom';
import { GetPlanningSessionResponse, updateAgendaActivityStep } from '../../../api/planning.api';
import { socket } from '../../../api/socket';
import { queryClient } from '../../../queryClient';
import colorTokens from '../../../constants/design_system/colors';
import { decodeSocketPayload } from '../../../hooks/socketPayload';
import { SocketEvent } from '../../../types/realtime-contract';

export const useAgenda = () => {
  const [checked, setChecked] = useState<number[]>([]);

  const planningSessionQuery = usePlanningSession();
  const [planningSessionId, setPlanningSessionId] = useState<number | null>(null);

  useEffect(() => {
    setPlanningSessionId(planningSessionQuery.data?.session?.id);
    const checkedSteps: number[] = [];
    planningSessionQuery.data?.session.PlanningSessionActivityGroups.forEach((group) => {
      group.PlanningSessionActivitySteps.forEach((step) => {
        if (step.status) {
          checkedSteps.push(step.id);
        }
      });
    });
    setChecked(checkedSteps);
  }, [planningSessionQuery.data]);

  const spaceId = useGetActualSpaceId();
  const updateStep = useCallback(
    async (step: PlanningSessionActivityStep) => {
      if (!checked.some((s) => s === step.id)) {
        setChecked((p) => [...p, step.id]);
      } else {
        setChecked((s) => s.filter((c) => c !== step.id));
      }

      if (planningSessionId && spaceId) {
        await updateAgendaActivityStep(String(planningSessionId), spaceId, step.id, !step.status);
      }
    },
    [planningSessionId, spaceId]
  );

  socket.on(SocketEvent.planningAgendaChanged, (raw: unknown) => {
    const { data } = decodeSocketPayload<{
      id: number;
      status: boolean;
      planningSessionActivityGroupId?: number;
    }>(raw);
    if (!data) return;
    if (data.status) {
      setChecked((s) => [...s.filter((c) => c !== data.id), data.id]);
    } else {
      setChecked((s) => s.filter((c) => c !== data.id));
    }

    // Update query
    queryClient.setQueryData(
      ['workspacePlanningSession', parseInt(spaceId as string)],
      (old: GetPlanningSessionResponse) => {
        const newPlaningSession = old;
        if (newPlaningSession) {
          const group = newPlaningSession.session.PlanningSessionActivityGroups.find(
            (group) => group.id === data.planningSessionActivityGroupId
          );
          if (group) {
            const step = group.PlanningSessionActivitySteps.find((step) => step.id === data.id);
            if (step) {
              step.status = data.status;
            }
          }
        }
        return newPlaningSession;
      }
    );
  });

  const renderAgenda = useMemo(() => {
    return (planningSessionQuery.data?.session.PlanningSessionActivityGroups || []).map((activityGroup, index) => {
      return activityGroup.PlanningSessionActivitySteps.map((s) => {
        const isChecked = checked.some((c) => c === s.id);
        return (
          <CheckRow
            key={activityGroup.id}
            title={activityGroup.name}
            step={s}
            index={index}
            checked={isChecked}
            onCheck={updateStep}
          />
        );
      });
    });
  }, [planningSessionQuery.data, checked]);

  return {
    renderAgenda,
    checked,
  };
};

const CheckRow = (props: {
  title: string;
  step: PlanningSessionActivityStep;
  index: number;
  checked: boolean;
  onCheck: Function;
}) => {
  const { title, step, index, checked, onCheck } = props;
  return useMemo(() => {
    return (
      <div className="flex gap-2 items-start mb-4" key={index} onClick={() => onCheck(step)}>
        <ConfigProvider
          theme={{
            components: {
              Checkbox: {
                colorPrimary: colorTokens.blue[600],
              },
            },
          }}
        >
          <Checkbox style={{ borderRadius: '50%' }} checked={checked} />
        </ConfigProvider>
        <p
          style={{
            textDecoration: checked ? 'line-through' : '',
          }}
          className="m-0 opacity-60 cursor-pointer text-brand-grey-dark"
        >
          <strong className="font-bold">{title}</strong>
          {step.name.replace(title, '')}
        </p>
      </div>
    );
  }, [step, index, checked]);
};
